/**
 * EmployeeHoverCard.jsx — Universal-Mitarbeiter-Vorschau.
 *
 * Wickelt beliebige Trigger-Elemente (typisch: Namen-Link) und zeigt
 * beim Hover eine kleine Schwebkarte mit dienstlichen Kontaktdaten +
 * Heute-Status. Klick öffnet das volle Kontakt-Modal.
 *
 * - Hover-Karte: 300ms openDelay (kein Spam beim Scrollen)
 * - Lazy-Load via /api/phonebook/employees/:id/contact-card
 * - In-Memory-Cache pro Session
 * - Datenschutz: nur dienstliche Daten
 */
import React, { useState } from 'react';
import {
  HoverCard, HoverCardContent, HoverCardTrigger,
} from '../components/ui/hover-card';
import { Phone, Smartphone, Mail, ExternalLink, Loader2 } from 'lucide-react';
import api from '../lib/api';
import PhonebookEmployeeDetail from '../pages/PhonebookEmployeeDetail';

const STATUS_DOT = {
  present: 'bg-emerald-500',
  scheduled: 'bg-blue-500',
  off_duty: 'bg-amber-500',
  vacation: 'bg-red-500',
  absent: 'bg-rose-500',
  free: 'bg-slate-400',
};

const STATUS_LABEL = {
  present: 'anwesend',
  scheduled: 'Schicht heute',
  off_duty: 'Schicht beendet',
  vacation: 'im Urlaub',
  absent: 'abwesend',
  free: 'nicht im Dienst',
};

// Cache pro Browser-Session, damit gleiche MA nicht mehrfach geladen werden
const _cache = new Map();
const _inflight = new Map();

const initials = (first, last) =>
  ((first?.[0] || '') + (last?.[0] || '')).toUpperCase() || '?';

const fetchPreview = (id) => {
  if (_cache.has(id)) return Promise.resolve(_cache.get(id));
  if (_inflight.has(id)) return _inflight.get(id);
  const p = api.get(`/phonebook/employees/${id}/contact-card`)
    .then((res) => {
      _cache.set(id, res.data);
      _inflight.delete(id);
      return res.data;
    })
    .catch((err) => {
      _inflight.delete(id);
      throw err;
    });
  _inflight.set(id, p);
  return p;
};

export const EmployeeHoverCard = ({ employeeId, children, asChild = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const ensureData = () => {
    if (!employeeId || data) return;
    setLoading(true);
    fetchPreview(employeeId)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  if (!employeeId) return children;

  return (
    <>
      <HoverCard openDelay={300} closeDelay={100}>
        <HoverCardTrigger asChild={asChild} onMouseEnter={ensureData} onFocus={ensureData}>
          {asChild ? children : <span>{children}</span>}
        </HoverCardTrigger>
        <HoverCardContent
          className="w-80 p-0 overflow-hidden"
          align="start"
          data-testid="employee-hover-card"
        >
          {loading && !data ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Lädt…
            </div>
          ) : data ? (
            <div className="text-sm">
              {/* Header */}
              <div className="flex items-start gap-3 p-3 border-b border-border/40">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                  {data.employee.avatar_url ? (
                    <img src={data.employee.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    initials(data.employee.first_name, data.employee.last_name)
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">
                    {data.employee.last_name}, {data.employee.first_name}
                  </div>
                  {data.employee.position && (
                    <div className="text-xs text-muted-foreground truncate">
                      {data.employee.position}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-0.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[data.today.status] || 'bg-slate-400'}`} />
                    {STATUS_LABEL[data.today.status] || data.today.status}
                    {data.today.shift && data.today.status !== 'vacation' && (
                      <span className="text-muted-foreground/80">
                        · {data.today.shift.start_time}–{data.today.shift.end_time}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick-Kontakte */}
              <div className="flex items-center justify-around p-2 border-b border-border/40 bg-muted/20">
                <a
                  href={data.employee.phone ? `tel:${data.employee.phone.replace(/\s+/g, '')}` : undefined}
                  className={`flex items-center justify-center w-9 h-9 rounded-full ${data.employee.phone ? 'bg-card border border-border hover:border-primary hover:text-primary' : 'opacity-30 cursor-not-allowed'}`}
                  title={data.employee.phone || 'kein Festnetz hinterlegt'}
                  onClick={(e) => !data.employee.phone && e.preventDefault()}
                >
                  <Phone className="h-4 w-4" />
                </a>
                <a
                  href={data.employee.mobile ? `tel:${data.employee.mobile.replace(/\s+/g, '')}` : undefined}
                  className={`flex items-center justify-center w-9 h-9 rounded-full ${data.employee.mobile ? 'bg-card border border-border hover:border-primary hover:text-primary' : 'opacity-30 cursor-not-allowed'}`}
                  title={data.employee.mobile || 'kein Mobil hinterlegt'}
                  onClick={(e) => !data.employee.mobile && e.preventDefault()}
                >
                  <Smartphone className="h-4 w-4" />
                </a>
                <a
                  href={data.employee.email ? `mailto:${data.employee.email}` : undefined}
                  className={`flex items-center justify-center w-9 h-9 rounded-full ${data.employee.email ? 'bg-card border border-border hover:border-primary hover:text-primary' : 'opacity-30 cursor-not-allowed'}`}
                  title={data.employee.email || 'keine E-Mail hinterlegt'}
                  onClick={(e) => !data.employee.email && e.preventDefault()}
                >
                  <Mail className="h-4 w-4" />
                </a>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-3 py-2 text-xs text-muted-foreground">
                <span className="truncate">
                  {data.location?.name || '—'}{data.employee.department ? ` · ${data.employee.department}` : ''}
                </span>
                <button
                  type="button"
                  onClick={() => setDetailOpen(true)}
                  className="inline-flex items-center gap-1 text-primary hover:underline shrink-0 ml-2"
                  data-testid="employee-hover-card-open-full"
                >
                  mehr <ExternalLink className="h-3 w-3" />
                </button>
              </div>
            </div>
          ) : (
            <div className="p-3 text-xs text-muted-foreground">Vorschau nicht verfügbar.</div>
          )}
        </HoverCardContent>
      </HoverCard>

      <PhonebookEmployeeDetail
        employeeId={detailOpen ? employeeId : null}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      />
    </>
  );
};

export default EmployeeHoverCard;
